import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { SiteHeader } from '../components/SiteHeader'
import { InviteAcceptPanel } from '../components/org/InviteAcceptPanel'
import { readInviteToken, stashInviteToken } from '../lib/inviteToken'

/** URL first, stash second — and the URL copy is stashed as it is read, so a
 *  reload or a mid-wizard signup does not lose it. Shared by both shells'
 *  invite routes so they cannot resolve the same link differently. */
export function tokenFrom(params: URLSearchParams): string | null {
  const fromUrl = params.get('token') || params.get('invite')
  if (fromUrl) { stashInviteToken(fromUrl); return fromUrl }
  return readInviteToken()
}

/**
 * /invite — the accept surface, reachable BOTH ways.
 *
 * The emailed link lands on `/signup?invite=<token>` (that string is baked
 * into `functions/src/org/invites.ts`). `InviteWatcher`, mounted at the shell
 * root, stashes the token from that query and sends an ALREADY-SIGNED-IN
 * visitor here — because `/signup` bounces a signed-in user to the catalog,
 * which for an invitee is a silent dead end. A signed-OUT visitor continues
 * into the wizard as normal and the stash is spent the moment their account
 * exists.
 *
 * Reading the token from the stash as well as the URL is what makes the
 * brand-new-human path work: the Firebase account is created MID-wizard and
 * the URL is long gone by the time there is a uid to accept with.
 *
 * ── THE TOKEN IS READ BEFORE THE FIRST PAINT, NOT AFTER IT ────────────────
 * It used to be read in a `useEffect`, so `token` was `null` for one frame on
 * EVERY visit and the panel painted "That invite link is incomplete" under a
 * red rejected seal before replacing it. A lazy `useState` initializer runs
 * during the first render, so a valid link never flashes a refusal it is not.
 */
export function OrgInvite() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [token, setToken] = useState<string | null>(() => tokenFrom(params))

  // Only for a LATER change of the query — an in-place navigation from one
  // invite link to another. The first read already happened, above.
  useEffect(() => {
    const fromUrl = params.get('token') || params.get('invite')
    if (fromUrl && fromUrl !== token) { stashInviteToken(fromUrl); setToken(fromUrl) }
  }, [params, token])

  return (
    <>
      <SiteHeader />
      <main className="min-h-[60vh] pb-16">
        <InviteAcceptPanel
          token={token}
          onSignIn={() => navigate('/signin')}
          onSignUp={() => navigate(`/signup${token ? `?invite=${encodeURIComponent(token)}` : ''}`)}
        />
      </main>
    </>
  )
}
